import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Inbox, RefreshCw, Mail, Phone, User, Baby, CalendarClock } from "lucide-react";
import { GuideMascot } from "../components/GuideMascot";
import { GUIDES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "dcc_admin_token";

const inputCls =
  "w-full rounded-2xl border-2 border-[#e2e8f0] bg-white px-4 py-3 font-body text-[#1E293B] outline-none transition-colors duration-200 focus:border-[#4A90E2] focus:ring-4 focus:ring-[#FFD166]/40";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setInquiries([]);
  }, []);

  const load = useCallback(async (tok) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/inquiries`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setInquiries(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        toast.error("Could not load inquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("Welcome back! 🌿");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed.");
    } finally {
      setLoggingIn(false);
    }
  };

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="min-h-[80vh] grid place-items-center bg-[#FDFBF7] px-5 py-16" data-testid="admin-login-page">
        <div className="w-full max-w-md rounded-[2rem] bg-white p-8 md:p-10 shadow-soft border border-black/5 text-center">
          <div className="flex justify-center mb-2"><GuideMascot src={GUIDES.book} size={110} ring="#4A90E2" /></div>
          <h1 className="font-head font-700 text-3xl text-[#1E293B]">Inquiry Inbox</h1>
          <p className="mt-2 font-body text-[#475569]">Enter the admin password to view enrollment inquiries.</p>
          <form onSubmit={login} className="mt-6 space-y-4 text-left">
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
              <input
                type="password"
                data-testid="admin-password-input"
                className={`${inputCls} pl-11`}
                placeholder="Admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-btn"
              className="w-full inline-flex items-center justify-center gap-2 font-head font-600 px-6 py-3.5 rounded-full bg-[#4A90E2] text-white shadow-soft-sm hover:bg-[#2f6fbf] transition-colors duration-200 disabled:opacity-60"
            >
              {loggingIn ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---------- Inbox screen ----------
  return (
    <div className="min-h-[80vh] bg-[#FDFBF7] px-5 md:px-8 py-12" data-testid="admin-inbox-page">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-2xl bg-[#4A90E2] grid place-items-center text-white"><Inbox size={22} /></span>
            <div>
              <h1 className="font-head font-700 text-2xl md:text-3xl text-[#1E293B]">Enrollment Inquiries</h1>
              <p className="font-body text-sm text-[#475569]">{inquiries.length} total submission{inquiries.length === 1 ? "" : "s"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(token)} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 font-body font-600 text-sm px-4 py-2.5 rounded-full bg-white border-2 border-[#e2e8f0] hover:bg-[#eaf2fc] transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 font-body font-600 text-sm px-4 py-2.5 rounded-full bg-white border-2 border-[#e2e8f0] hover:bg-[#fdeef1] transition-colors">
              <LogOut size={16} /> Log Out
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-4">
          {loading && inquiries.length === 0 && (
            <div className="text-center py-16 font-body text-[#475569]">Loading inquiries…</div>
          )}
          {!loading && inquiries.length === 0 && (
            <div className="text-center py-16 rounded-3xl bg-white border border-black/5 shadow-soft-sm" data-testid="admin-empty">
              <div className="flex justify-center mb-3"><GuideMascot src={GUIDES.wave} size={110} ring="#FFD166" /></div>
              <p className="font-head font-600 text-xl text-[#1E293B]">No inquiries yet</p>
              <p className="font-body text-[#475569] mt-1">New family submissions will appear here.</p>
            </div>
          )}
          {inquiries.map((q) => (
            <div key={q.id} className="rounded-3xl bg-white border border-black/5 shadow-soft-sm p-6" data-testid="admin-inquiry-card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <User size={18} className="text-[#4A90E2]" />
                  <span className="font-head font-600 text-lg text-[#1E293B]">{q.parent_name}</span>
                </div>
                <span className="inline-flex items-center gap-1.5 font-body text-xs text-[#475569] bg-[#FDFBF7] rounded-full px-3 py-1.5">
                  <CalendarClock size={14} /> {formatDate(q.created_at)}
                </span>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 font-body text-sm text-[#475569]">
                <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-[#4A90E2]"><Mail size={15} /> {q.email}</a>
                <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-[#4A90E2]"><Phone size={15} /> {q.phone}</a>
                {(q.child_name || q.child_age) && (
                  <span className="flex items-center gap-2"><Baby size={15} /> {q.child_name || "—"}{q.child_age ? ` · ${q.child_age}` : ""}</span>
                )}
                {q.schedule && <span className="flex items-center gap-2"><CalendarClock size={15} /> {q.schedule}</span>}
              </div>
              {q.program && (
                <span className="inline-block mt-3 font-body text-xs font-600 text-[#2f6fbf] bg-[#eaf2fc] rounded-full px-3 py-1.5">{q.program}</span>
              )}
              {q.message && (
                <p className="mt-3 font-body text-sm text-[#1E293B] bg-[#FDFBF7] rounded-2xl p-4 leading-relaxed">{q.message}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Admin;
